import {
  clearStoredSession,
  getStoredAccessToken,
  getStoredRefreshToken,
  updateStoredTokens,
} from "../../utils/authStorage";
import {
  emitAccessDeniedEvent,
  emitAuthExpiredEvent,
} from "../../utils/authEvents";
import type {
  ApiHttpMethod,
  WarehouseApiModuleContext,
} from "./WarehouseApiModuleContext";

type ExtractApiError = (payload: unknown) => string | null;
type AuthRefreshResult = "success" | "missing_token" | "failed";

interface WarehouseHttpClientDependencies {
  extractApiError: ExtractApiError;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function resolveBaseUrl(): string {
  const envUrl = String(
    import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || "",
  ).trim();
  if (envUrl) {
    return envUrl.replace(/\/+$/, "");
  }

  if (typeof window !== "undefined") {
    return window.location.origin;
  }

  return "http://localhost:3001";
}

export class WarehouseHttpClient implements WarehouseApiModuleContext {
  private readonly baseUrl: string;

  private refreshInFlight: Promise<AuthRefreshResult> | null = null;

  constructor(
    private readonly deps: WarehouseHttpClientDependencies,
    baseUrl = resolveBaseUrl(),
  ) {
    this.baseUrl = baseUrl;
  }

  async getJson<T>(path: string): Promise<T> {
    return this.sendJson<T>(path, "GET");
  }

  async sendJson<T>(
    path: string,
    method: ApiHttpMethod,
    body?: unknown,
  ): Promise<T> {
    const response = await this.fetchWithAuth(path, method, body);
    const payload = await this.readJsonBody<T>(response);

    if (!response.ok) {
      const apiError = this.deps.extractApiError(payload);
      if (response.status === 403 && !this.isAuthPath(path)) {
        emitAccessDeniedEvent({
          path,
          message:
            apiError ||
            "Недостаточно прав для выполнения операции. Обратитесь к администратору.",
        });
      }

      throw new Error(
        apiError ||
          `Ошибка API: ${response.status} ${response.statusText || "Unknown"}`,
      );
    }

    return payload;
  }

  async fetchWithAuth(
    path: string,
    method: ApiHttpMethod,
    body?: unknown,
  ): Promise<Response> {
    const includeAuth = !this.isPublicAuthPath(path);
    let response = await this.request(path, method, body, includeAuth);
    let authFailureHandled = false;

    if (response.status === 401 && !this.isAuthPath(path) && includeAuth) {
      const refreshResult = await this.refreshAuthTokens();
      if (refreshResult === "success") {
        response = await this.request(path, method, body, true);
      } else {
        authFailureHandled = true;
        this.handleUnauthorized(
          refreshResult === "missing_token"
            ? "missing_token"
            : "refresh_failed",
        );
      }
    }

    if (
      response.status === 401 &&
      !this.isAuthPath(path) &&
      !authFailureHandled
    ) {
      this.handleUnauthorized("unauthorized");
    }

    return response;
  }

  private async readJsonBody<T>(response: Response): Promise<T> {
    const raw = await response.text();
    if (!raw.trim()) {
      return {} as T;
    }

    return JSON.parse(raw) as T;
  }

  private buildHeaders(includeAuth: boolean): Headers {
    const headers = new Headers({
      "Content-Type": "application/json",
    });

    const token = includeAuth ? getStoredAccessToken() : null;
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    return headers;
  }

  private request(
    path: string,
    method: ApiHttpMethod,
    body: unknown,
    includeAuth: boolean,
  ): Promise<Response> {
    return fetch(`${this.baseUrl}${path}`, {
      method,
      headers: this.buildHeaders(includeAuth),
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  private async refreshAuthTokens(): Promise<AuthRefreshResult> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    this.refreshInFlight = (async () => {
      const refreshToken = getStoredRefreshToken();
      if (!refreshToken) {
        return "missing_token";
      }

      try {
        const response = await this.request(
          "/api/auth/refresh",
          "POST",
          { refreshToken },
          false,
        );
        const payload = await this.readJsonBody<unknown>(response);

        if (!response.ok || !isRecord(payload)) {
          if (response.status === 401) {
            clearStoredSession();
          }
          return "failed";
        }

        const accessToken = String(payload.accessToken || "").trim();
        const nextRefreshToken = String(payload.refreshToken || "").trim();
        const expiresIn = Number(payload.expiresIn);

        if (!accessToken || !nextRefreshToken || !Number.isFinite(expiresIn)) {
          return "failed";
        }

        updateStoredTokens({
          accessToken,
          refreshToken: nextRefreshToken,
          expiresIn,
        });

        return "success";
      } catch {
        return "failed";
      } finally {
        this.refreshInFlight = null;
      }
    })();

    return this.refreshInFlight;
  }

  private isAuthPath(path: string): boolean {
    return path.startsWith("/api/auth/");
  }

  private isPublicAuthPath(path: string): boolean {
    return (
      path === "/api/auth/login" ||
      path === "/api/auth/register" ||
      path === "/api/auth/telegram" ||
      path === "/api/auth/telegram/widget" ||
      path === "/api/auth/google" ||
      path === "/api/auth/apple" ||
      path === "/api/auth/refresh"
    );
  }

  private handleUnauthorized(
    reason: "missing_token" | "refresh_failed" | "unauthorized",
  ): void {
    clearStoredSession();
    emitAuthExpiredEvent({ reason });
  }
}
